import { registerIdentityCache } from "./auth/query-identity";
import i18n from "./i18n";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider, MutationCache, hydrate } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import { toast } from "./lib/toast";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1 },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Mutacija sa vlastitim onError sama prikazuje grešku (toast ili inline);
      // globalni toast bi je samo duplirao.
      if (mutation.options.onError) return;
      toast.error(formatMutationError(error));
    },
  }),
});

registerIdentityCache(queryClient, () => {
  // Hide retained observer/form state immediately while the document restarts.
  document.documentElement.style.visibility = "hidden";
  window.location.reload();
});

function formatMutationError(error: Error): string {
  const message = error.message || "";
  if (message.startsWith("BILLING_INFO_MISSING")) {
    return i18n.t("therapist.dashboard.subscriptionBillingMissingToast");
  }
  return message || i18n.t("common.error");
}

// Hydrate react-query state from SSR (if present) before any component reads it.
const ssr = (typeof window !== "undefined" ? window.__SSR__ : undefined);
if (ssr?.query) {
  try {
    hydrate(queryClient, ssr.query);
  } catch (err) {
    console.warn("[ssr] hydration of react-query state failed:", err);
  }
}

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);

if ("serviceWorker" in navigator && !import.meta.hot) {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}

if (import.meta.hot) {
  // HMR (dev): always createRoot, never SSR in dev mode
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else if (ssr) {
  try {
    hydrateRoot(elem, app);
  } catch (err) {
    console.warn("[ssr] hydrateRoot failed, recovering with createRoot:", err);
    createRoot(elem).render(app);
  }
} else {
  createRoot(elem).render(app);
}
